import { HelpHero } from 'helphero';
import { action, observable } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import ReactModal from 'react-modal';

import { StringKeyMap } from '../../../adl-gen/common';
import { DbKey, WithDbId } from '../../../adl-gen/common/db';
import {
  AppUser,
  CompetencyBarsSlideout,
  CompetencySkillTrainingOffering,
  RoleRecommendationMethod,
  TrainingOffering,
  TrainingOfferingCategory,
  WorkRole
} from '../../../adl-gen/totalinteraction/futurefit/db';
import { Competency, SingleUserTrainingRecommendations } from '../../../adl-gen/totalinteraction/futurefit/frontend';
import { withI18n, WithI18nProps } from '../../../internationalization/i18n-hoc';
import { i18next } from '../../../internationalization/i18next';
import { Loader, MaybeLoading } from '../../../loader/loader';
import { LoggedIn } from '../../../stores/identity-store';
import { ManagerInfo, SelectedResults } from '../../../stores/manager-quiz-results-store';
import { PlotKeyToggleState } from '../../../stores/plot-key-toggle-state';
import { CategoryOption, LearningType, QuizResultsHelper } from '../../../stores/quiz-results-store';
import { compareByGet } from '../../../util/utils';
import { PageLayoutFn } from '../../common/page-layout/page-layout';
import { TabContainer } from '../../common/tab-container/tab-container';
import {
  CompetencyGapReport,
  computeDiffCategory,
  diffCategoryToStatus
} from '../../components/competency-gap-report/competency-gap-report';
import { FilterOption } from '../../components/filters/common/enabled-disabled-map';
import { CompetencyFilter } from '../../components/filters/competency-filter/competency-filter';
import { LearningTypeFilter } from '../../components/filters/learning-type-filter/learning-type-filter';
import { RecommendationCategoryFilter } from '../../components/filters/recommendation-category-filter/recommendation-category-filter';
import { UserGroupsFilter } from '../../components/filters/user-groups-filter/user-groups-filter';
import {
  ManagerReportTable,
  ManagerReportTableProps
} from '../../components/manager-report-table/manager-report-table';
import { Recommendations } from '../../components/recommendations/recommendations';
import { SectionAccordion } from '../../components/section-accordion/section-accordion';
import { SelectOption } from '../../widgets/select/select';
import { GlobalPrint } from '../quiz-results-screen/quiz-results-screen';
import { QuizSummary } from '../quiz-summary/quiz-summary';

const styles = require('./manager-results-screen.css');

// TODO: (dong): pull out the required parts of the quizResultsSelectionStore
// and populate them accordingly at the callsite
interface ManagerResultsScreenProps extends WithI18nProps {
  /** Page layout to contain body */
  pageLayout: PageLayoutFn;
  page: 'profile' | 'guidance' | 'recommendations' | 'print';
  identity: LoggedIn;

  loading: MaybeLoading<ManagerInfo>;

  plotKeyToggleState: PlotKeyToggleState;
  showGroupsModal?: boolean;

  applyFilter(selected: SelectedResults): void;
  selectRole(role: string): void;
  /// Enhanced Recommendations
  selectCategories(selected: TrainingOfferingCategory[]): void;
  selectCompetencies(selected: string[]): void;
  selectPriorities(selected: string[]): void;
  resetAll(): void;

  walkThroughFn?(page: string): void;
  walkThroughMgr?: HelpHero;

  onLaunchTrainingOffering?(offering: WithDbId<TrainingOffering>, userId: DbKey<AppUser>): string;
}

ReactModal.setAppElement('#root');

type ModalState = 'none' | 'usersgroups' | 'roles' | 'categories' | 'competencies' | 'priorities';

@observer
export class ManagerResultsScreen0 extends React.Component<ManagerResultsScreenProps> {
  @observable modalState: ModalState = 'none';

  /// Suppress traffic light report if more than this number of users (ie. columns)
  readonly MAX_TRAFFIC_LIGHT_COLS = 15;

  @action
  setModalState = (modalState: ModalState) => {
    this.modalState = modalState;
  };

  @action
  onApplyFilters = (selected: SelectedResults) => {
    this.props.applyFilter(selected);
    this.setModalState('none');
  };

  @action
  onSelectCategories = (selected: TrainingOfferingCategory[]) => {
    this.props.selectCategories(selected);
    this.setModalState('none');
  };

  @action
  onSelectCompetencies = (qr: QuizResultsHelper) => (selected: string[]) => {
    /// Find skills assoc with these competencies
    const skills = selected.map(qr.getSkillsByCompetency).reduce((arr, next) => arr.concat(next), []);
    this.props.selectCompetencies([...selected, ...skills]);
    this.setModalState('none');
  };

  @action
  onSelectPriorities = (selected: string[]) => {
    this.props.selectPriorities(selected);
    this.setModalState('none');
  };

  @action
  onResetAll = () => {
    this.props.resetAll();
    this.setModalState('none');
  };

  setUpWalkThrough = (state: ManagerInfo) => {
    const { identity, page, walkThroughFn, walkThroughMgr } = this.props;
    const { qr, guidanceAvailable, recommendationsAvailable } = state;
    const defaultRole = qr.getDefaultRole();
    const recommendationMethod = defaultRole
      ? defaultRole.value.recommendationMethod === RoleRecommendationMethod.roleRange
        ? 'roleRange'
        : 'relevance'
      : 'relevance';
    const roleSelected = qr.isRoleSelected();
    if (walkThroughMgr) {
      if (qr.allUsers().length > this.MAX_TRAFFIC_LIGHT_COLS) {
        /// Signal missing traffic light report to HelpHero walkthroughs
        /// Do this before setting walkthrough ID
        walkThroughMgr.update({ noTrafficLights: true });
      } else {
        walkThroughMgr.update({ noTrafficLights: false });
      }
      walkThroughMgr.update({ noGuidance: !guidanceAvailable });
      walkThroughMgr.update({ noRecommendations: !recommendationsAvailable });
      walkThroughMgr.update({ noSkillBars: qr.quiz.competencyBars === CompetencyBarsSlideout.noSkills });
      walkThroughMgr.update({ noCompetencyBarsSlideout: qr.quiz.competencyBars === CompetencyBarsSlideout.none });
      walkThroughMgr.update({ recommendationMethod });
      // TODO: Name pluralized so as not to break walkhroughs 
      walkThroughMgr.update({ rolesSelected: roleSelected });
      /// TODO: Update walkthroughs
      walkThroughMgr.update({ enhancedRecommendations: identity.appClient.singleTabRecommendations });
    }
    if (walkThroughFn) {
      walkThroughFn(page);
    }
  };

  getRecommendationMethod = (state: ManagerInfo): RoleRecommendationMethod => {
    const qr = state.qr;
    const defaultRole = qr.getDefaultRole();
    if (defaultRole) {
      return defaultRole.value.recommendationMethod;
    }
    /// default to relevance
    return RoleRecommendationMethod.relevance;
  };

  setRoleTabInfo = (
    aggregateTrainingRecommendations: SingleUserTrainingRecommendations,
    title: string,
    roleId: DbKey<WorkRole>,
    key: number,
    qr: QuizResultsHelper,
    categories: CategoryOption[],
    tags: SelectOption[]
  ) => {
    const { identity, onLaunchTrainingOffering } = this.props;
    const userTraining = this.filterRecommendationsOnRole(aggregateTrainingRecommendations, roleId);
    if (userTraining) {
      return {
        key,
        title,
        content: (
          <Recommendations
            userTraining={userTraining}
            categories={categories}
            categoryFilter={'external-controlled'}
            priorityFilter={qr.priorityFilter}
            // categoryFilter={"none"}
            enabled={identity.appClient.recommendationsVisible}
            launchUrl={onLaunchTrainingOffering}
            tags={tags}
          />
        ),
        selected: true,
        disabled: false
      };
    } else {
      return {
        key,
        title,
        content: '',
        selected: false,
        disabled: true
      };
    }
  };

  filterRecommendationsOnRole = (
    aggregateTrainingRecommendations: SingleUserTrainingRecommendations,
    roleId: DbKey<WorkRole>
  ): SingleUserTrainingRecommendations | undefined => {
    const { user: viewer, data } = aggregateTrainingRecommendations;
    const recommendations = viewer.recommendations.filter(
      rec => rec.result.kind === 'roleScore' && rec.result.value.workRoleId === roleId
    );
    if (recommendations.length > 0) {
      const roleCompetencySkillMap: StringKeyMap<string, CompetencySkillTrainingOffering[]> = {};
      for (const [k, v] of Object.entries(viewer.competencySkillMap)) {
        const includedRec = recommendations.find(rec =>
          v.map(item => item.value.trainingOfferingId).includes(rec.trainingOfferingId)
        );
        if (includedRec) {
          roleCompetencySkillMap[k] = viewer.competencySkillMap[k];
        }
      }
      return {
        user: {
          appUserId: viewer.appUserId,
          competencySkillMap: roleCompetencySkillMap,
          recommendations
        },
        data
      };
    } else {
      return undefined;
    }
  };

  /// Run this after SingleUserTrainingRecommendations.data has been used for anything else
  /// (eg. finding out whether a category is available) just before rendering
  filterAvailableTrainingOfferings = (state: ManagerInfo): StringKeyMap<string, TrainingOffering> => {
    // TODO: Curry these using closures
    const filteredData = this.filterAvailableTrainingOfferingsOnCategory(state);
    return this.filterAvailableTrainingOfferingsOnCompetency(state, filteredData);
  };

  filterAvailableTrainingOfferingsOnCategory = (state: ManagerInfo): StringKeyMap<string, TrainingOffering> => {
    const { filteredCategories, aggregateTrainingOfferings } = state;
    const { data } = aggregateTrainingOfferings;
    if (filteredCategories.length > 0) {
      const filteredData: StringKeyMap<string, TrainingOffering> = Object.entries(data).reduce(
        (acc: StringKeyMap<string, TrainingOffering>, [k, v]) => {
          if (filteredCategories.includes(v.category)) {
            acc[k] = v;
          }
          return acc;
        },
        {}
      );
      return filteredData;
    }
    return data;
  };
  filterAvailableTrainingOfferingsOnCompetency = (
    state: ManagerInfo,
    prefilteredData: StringKeyMap<string, TrainingOffering>
  ) => {
    const { filteredCompetencies, aggregateTrainingOfferings } = state;
    const data = prefilteredData;
    if (filteredCompetencies.length > 0) {
      // Pull out competencySkillsMap into flat array and map to IDs.
      // Assume this map has already been prefiltered using the filtered competencies
      const list = Object.values(aggregateTrainingOfferings.user.competencySkillMap)
        .reduce((acc, next) => {
          return acc.concat(next);
        }, [])
        .map(off => off.value.trainingOfferingId);
      const filteredData: StringKeyMap<string, TrainingOffering> = Object.entries(data).reduce(
        (acc: StringKeyMap<string, TrainingOffering>, [k, v]) => {
          if (list.includes(k)) {
            acc[k] = v;
          }
          return acc;
        },
        {}
      );
      return filteredData;
    }
    return data;
  };

  getRowCollectionPropsFromCompetency(
    qr: QuizResultsHelper,
    competencyId: string,
    competency: Competency
  ): ManagerReportTableProps['content']['main'][0] {
    const { t } = this.props;
    const skills = qr.getSkillsByCompetency(competencyId);
    return {
      key: competencyId,
      header: t(competency.name),
      content: qr
        .getAppUsers()
        .map(appUserId => renderStatus(diffCategoryToStatus(computeDiffCategory(qr, { appUserId, competencyId })))), // For each person and competency, get diff category
      sub: skills.sort(compareByGet(skillId => qr.skill(skillId).name)).map(skillId => ({
        tooltip: t(qr.skill(skillId).definition),
        header: t(qr.skill(skillId).name),
        content: qr
          .getAppUsers()
          .map(appUserId => renderStatus(diffCategoryToStatus(computeDiffCategory(qr, { appUserId, skillId })))) // For each person, skill and competency, get diff category
      }))
    };
  }

  getContentFromQuizResults(qr: QuizResultsHelper): ManagerReportTableProps['content'] {
    const { t } = this.props;
    return {
      main: qr
        .getCompetencies(t)
        .map((c, i) => this.getRowCollectionPropsFromCompetency(qr, qr.getCompetencyKeys(t)[i], c))
    };
  }

  renderLoaded = observer((state: ManagerInfo) => {
    const { page, t } = this.props;
    const { aggregateTrainingOfferings, guidanceAvailable, qr, recommendationsAvailable } = state;
    const { singleTabRecommendations } = this.props.identity.appClient;
    if (page === 'print') {
      return (
        <>
          <GlobalPrint />
          {this.renderReport(qr)}
          {guidanceAvailable && this.renderGuidancePage(qr)}
          {recommendationsAvailable && this.renderRecommendationsPage(state)}
        </>
      );
    }

    this.setUpWalkThrough(state);

    /// Get all available (different) trainingOfferings categories
    const availableTrainingOfferingsCategories = Object.values(aggregateTrainingOfferings.data)
      .map(off => off.category)
      .reduce((acc: TrainingOfferingCategory[], next) => {
        if (!acc.includes(next)) {
          acc.push(next);
        }
        return acc;
      }, []);
    /// Get all available trainingOfferings competencies
    const flatCompetencySkillsMap = Object.values(aggregateTrainingOfferings.user.competencySkillMap).reduce(
      (acc, next) => {
        return acc.concat(next);
      },
      []
    );
    const availableCompetencies: string[] = flatCompetencySkillsMap
      .reduce((acc: string[], next) => {
        if (Object.keys(aggregateTrainingOfferings.data).includes(next.value.trainingOfferingId)) {
          if (next.kind === 'competencyTrainingOffering') {
            acc.push(next.value.competencyId);
          } else {
            const skill = qr.skill(next.value.skillId);
            if (skill) {
              acc.push(skill.competencyId);
            }
          }
        }
        return acc;
      }, [])
      // Unique
      .filter((val, idx, arr) => arr.indexOf(val) === idx);

    return (
      <this.props.pageLayout
        title={`${t(qr.quiz.title)} - ${i18next.t('title.manager-results', 'Team Results')}`}
        onClickFilter={() => this.setModalState('usersgroups')}
        onClickCategories={
          page === 'recommendations' && singleTabRecommendations ? () => this.setModalState('categories') : undefined
        }
        /// SS-759: Enable these filters even for renderTabbedPriority
        onClickCompetencies={
          page === 'recommendations' && this.getRecommendationMethod(state) === RoleRecommendationMethod.relevance //&& singleTabRecommendations
            ? () => this.setModalState('competencies')
            : undefined
        }
        onClickPriorities={
          page === 'recommendations'
            ? () => this.setModalState('priorities')
            : undefined
        }
        onClickResetAll={
          page === 'recommendations'
            ? () => this.onResetAll()
            : undefined
        }
      >
        {this.modalState === 'usersgroups' && this.renderFilter(state)}
        {this.modalState === 'categories' && this.renderCategoryFilter(state, availableTrainingOfferingsCategories)}
        {this.modalState === 'competencies' && this.renderCompetencyFilter(state, availableCompetencies)}
        {this.modalState === 'priorities' && this.renderPriorityFilter(state)}
        {page === 'profile' && this.renderReport(qr)}
        {page === 'guidance' && state.guidanceAvailable && this.renderGuidancePage(qr)}
        {page === 'recommendations' && state.recommendationsAvailable && this.renderRecommendationsPage(state)}
      </this.props.pageLayout>
    );
  });

  renderFilter(state: ManagerInfo) {
    return (
      <UserGroupsFilter
        initial={state.selectedResults}
        usersGroupsMap={state.usersGroupsMap}
        usersCompleted={state.qr.getUsersCompleted()}
        onDone={this.onApplyFilters}
        onCancel={() => this.setModalState('none')}
      />
    );
  }

  renderCategoryFilter(state: ManagerInfo, availableCategories: TrainingOfferingCategory[]) {
    const { filteredCategories } = state;
    const allCategoryIds = [
      TrainingOfferingCategory.elearning,
      TrainingOfferingCategory.face2face,
      TrainingOfferingCategory.virtual
    ];
    const defaultFilterOptionSet: FilterOption<TrainingOfferingCategory>[] = allCategoryIds.map(cat => ({
      visibility: 'disabled',
      value: cat
    }));
    const availableFilterOptionSet: FilterOption<TrainingOfferingCategory>[] = defaultFilterOptionSet.map(cat => {
      if (availableCategories.includes(cat.value)) {
        return { visibility: 'default', value: cat.value };
      }
      return cat;
    });
    const initList = filteredCategories.length === 0 ? availableCategories : filteredCategories;
    return (
      <RecommendationCategoryFilter
        initial={initList}
        options={availableFilterOptionSet}
        onDone={this.onSelectCategories}
        onCancel={() => this.setModalState('none')}
      />
    );
  }

  renderCompetencyFilter(state: ManagerInfo, availableCompetencies: string[]) {
    const { t } = this.props;
    const { qr, filteredCompetencies } = state;
    const competencyMap: StringKeyMap<DbKey<Competency>, Competency> = qr
      .getCompetencyKeys(t)
      .reduce((map: StringKeyMap<DbKey<Competency>, Competency>, next) => {
        map[next] = qr.competency(next);
        return map;
      }, {});
    const defaultFilterOptionSet: FilterOption<string>[] = qr
      .getCompetencyKeys(t)
      .map(c => ({ visibility: 'disabled', value: c }));
    const availableFilterOptionSet: FilterOption<string>[] = defaultFilterOptionSet.map(cat => {
      if (availableCompetencies.includes(cat.value)) {
        return { visibility: 'default', value: cat.value };
      }
      return cat;
    });
    const initList = filteredCompetencies.length === 0 ? availableCompetencies : filteredCompetencies;
    return (
      <CompetencyFilter
        initial={initList}
        options={availableFilterOptionSet}
        competencyMap={competencyMap}
        onDone={this.onSelectCompetencies(qr)}
        onCancel={() => this.setModalState('none')}
      />
    );
  }

  renderPriorityFilter(state: ManagerInfo) {
    const { filteredPriorities } = state;
    const allPriorities: LearningType[] = ['recommendations', 'all-learning'];
    const defaultLearning: LearningType[] = ['recommendations'];
    const initList = filteredPriorities.length === 0 ? defaultLearning : filteredPriorities;
    return (
      <LearningTypeFilter
        initial={initList}
        allPriorities={allPriorities}
        onDone={this.onSelectPriorities}
        onCancel={() => this.setModalState('none')}
      />
    );
  }

  renderGuidancePage(qr: QuizResultsHelper) {
    return (
      <div>
        <CompetencyGapReport qr={qr} />
      </div>
    );
  }

  renderRecommendationsPage(state: ManagerInfo) {
    const { aggregateTrainingOfferings, qr } = state;

    /// Filter 'All Available' on Category and Competency here if required

    const defaultRole = qr.getDefaultRole();
    if (defaultRole && defaultRole.value.recommendationMethod === RoleRecommendationMethod.roleRange) {
      return this.renderRoleRangeRecommendationsPage(qr, aggregateTrainingOfferings);
    } else {
      const availableTrainingOfferingMap = this.filterAvailableTrainingOfferings(state);
      if (this.props.identity.appClient.singleTabRecommendations) {
        return this.renderSinglePriorityRecommendationsPage(
          { ...aggregateTrainingOfferings, data: availableTrainingOfferingMap },
          qr
        );
      } else {
        return this.renderTabbedPriorityRecommendationsPage(
          { ...aggregateTrainingOfferings, data: availableTrainingOfferingMap },
          qr
        );
        // return this.renderTabbedPriorityRecommendationsPage(aggregateTrainingOfferings, qr);
      }
    }
  }

  renderSinglePriorityRecommendationsPage(
    aggregateTrainingRecommendations: SingleUserTrainingRecommendations,
    qr: QuizResultsHelper
  ) {
    return (
      <Recommendations
        userTraining={aggregateTrainingRecommendations}
        enabled={this.props.identity.appClient.recommendationsVisible}
        priorityFilter={qr.priorityFilter}
        launchUrl={this.props.onLaunchTrainingOffering}
      /// undefined
      // categoryFilter={"none"}
      // tags={competencyLabels}
      />
    );
  }

  renderTabbedPriorityRecommendationsPage(
    aggregateTrainingRecommendations: SingleUserTrainingRecommendations,
    qr: QuizResultsHelper
  ) {
    // FF2-445: Select first tab by default unless it's disabled by the lack of that category being available
    let eLearningIncluded: boolean = false;
    let virtualIncluded: boolean = false;
    let face2faceIncluded: boolean = false;
    for (const v of Object.values(aggregateTrainingRecommendations.data)) {
      if (v.category === TrainingOfferingCategory.elearning) {
        eLearningIncluded = true;
      } else if (v.category === TrainingOfferingCategory.virtual) {
        virtualIncluded = true;
      } else if (v.category === TrainingOfferingCategory.face2face) {
        face2faceIncluded = true;
      }
    }
    /// TODO Use Competencies for Pills
    /// This doesn't work yet - need to fix CompetencySkillsMap or alternative (maybe cf ManagerResults)
    // const competencyLabels: SelectOption[] = qr.getTagOrCategoryOptions(t);
    return (
      <TabContainer
        items={[
          {
            /// You can't set JSX elements as props explicitly here, because by the time they get to TabPanel, you get
            /// "Objects are not valid as a React child". This is a TS thing I think
            key: 0,
            title: i18next.t('recommendations-categories.elearning', 'Self-Paced/eLearning'),
            content: (
              <Recommendations
                userTraining={aggregateTrainingRecommendations}
                enabled={this.props.identity.appClient.recommendationsVisible}
                categoryFilter={TrainingOfferingCategory.elearning}
                priorityFilter={qr.priorityFilter}
                launchUrl={this.props.onLaunchTrainingOffering}
              // tags={competencyLabels}
              />
            ),
            selected: eLearningIncluded,
            disabled: !eLearningIncluded
          },
          {
            key: 1,
            title: i18next.t('recommendations-categories.virtual', 'Virtual Live'),
            content: (
              <Recommendations
                userTraining={aggregateTrainingRecommendations}
                enabled={this.props.identity.appClient.recommendationsVisible}
                categoryFilter={TrainingOfferingCategory.virtual}
                priorityFilter={qr.priorityFilter}
                launchUrl={this.props.onLaunchTrainingOffering}
              />
            ),
            selected: !eLearningIncluded || virtualIncluded,
            disabled: !virtualIncluded
          },
          {
            key: 2,
            title: i18next.t('recommendations-categories.face2face', 'Face to Face'),
            content: (
              <Recommendations
                userTraining={aggregateTrainingRecommendations}
                enabled={this.props.identity.appClient.recommendationsVisible}
                categoryFilter={TrainingOfferingCategory.face2face}
                priorityFilter={qr.priorityFilter}
                launchUrl={this.props.onLaunchTrainingOffering}
              />
            ),
            selected: !eLearningIncluded || face2faceIncluded,
            disabled: !face2faceIncluded
          }
        ]}
        selectedTabKey={eLearningIncluded ? 0 : virtualIncluded ? 1 : face2faceIncluded ? 2 : 0}
      />
    );
  }

  renderRoleRangeRecommendationsPage(
    qr: QuizResultsHelper,
    aggregateTrainingRecommendations: SingleUserTrainingRecommendations
  ) {
    const { identity, t } = this.props;
    // Pull ComparisonRoles and Competencies

    /// Use Competencies (or Skills if appropriate) for Pills
    const categoryTags = qr.getTagOptions(t);
    /// Use Competencies for Category (accordion) data
    const categoryLabelsAndRatios = qr.getCategoryOptions(t);
    // Only use these if roles are actually selected for comparison.
    const selectedRoleId = qr.getSelectedRoleId();
    if (selectedRoleId) {
      const roleMap = qr.getAllRoles();
      const defaultRoleItem = this.setRoleTabInfo(
        aggregateTrainingRecommendations,
        t(qr.quiz.workRole.name),
        qr.quiz.workRoleId,
        0,
        qr,
        categoryLabelsAndRatios,
        categoryTags
      );
      const otherRoleItems = selectedRoleId !== qr.quiz.workRoleId
        ? [this.setRoleTabInfo(
          aggregateTrainingRecommendations,
          t(roleMap[selectedRoleId].workRole.value.name),
          selectedRoleId,
          1,
          qr,
          categoryLabelsAndRatios,
          categoryTags
        )] : [];
      return (
        <TabContainer
          items={[defaultRoleItem, ...otherRoleItems]}
          selectedTabKey={defaultRoleItem.selected ? 0 : otherRoleItems.findIndex(r => r.selected) + 1}
        />
      );
    } else {
      /// Suppress tabs altogether - BUT only serve recommendations for the default role
      const defaultUserTraining = this.filterRecommendationsOnRole(
        aggregateTrainingRecommendations,
        qr.quiz.workRoleId
      );
      if (defaultUserTraining) {
        return (
          <Recommendations
            userTraining={defaultUserTraining}
            categories={categoryLabelsAndRatios}
            categoryFilter={'external-controlled'}
            priorityFilter={qr.priorityFilter}
            enabled={identity.appClient.recommendationsVisible}
            launchUrl={this.props.onLaunchTrainingOffering}
            tags={categoryTags}
          />
        );
      } else {
        return null;
      }
    }
  }

  renderSummary(qr: QuizResultsHelper) {
    return (
      <div className={styles.summaryContainer}>
        <QuizSummary
          qr={qr}
          plotKeyToggleState={this.props.plotKeyToggleState}
          displayHeadlineRatio={this.props.identity.appClient.displayHeadlineRatio}
          displayHeadlineText={this.props.identity.appClient.displayHeadlineText}
        />
      </div>
    );
  }

  renderTable(qr: QuizResultsHelper) {
    return <ManagerReportTable columns={qr.allUsers()} content={this.getContentFromQuizResults(qr)} />;
  }

  renderReport(qr: QuizResultsHelper): JSX.Element {
    /// NB Affects walkthroughs
    if (qr.allUsers().length > this.MAX_TRAFFIC_LIGHT_COLS) {
      return this.renderSummary(qr);
    } else {
      return (
        <>
          {this.renderSummary(qr)}
          <SectionAccordion
            headerTitle={i18next.t('traffic-light-report-title', `Team Traffic Light Report`)}
            expandedContent={this.renderTable(qr)}
            isOpen={false}
          />
        </>
      );
    }
  }

  render() {
    return <Loader state={this.props.loading} LoadedComponent={this.renderLoaded} />;
  }
}

export const ManagerResultsScreen = withI18n(ManagerResultsScreen0);

export function renderStatus(kind: 'improve' | 'enhance' | 'continue') {
  if (kind === 'improve') {
    return (
      <div className={styles.statusContainer}>
        <svg id="Layer_1" data-name="Layer 1" viewBox="0 0 24 24">
          <defs>
            <clipPath id="clip-path">
              <circle cx="12" cy="12" r="12" style={{ fill: 'none' }} />
            </clipPath>
          </defs>
          <g style={{ clipPath: 'url(#clip-path)' }}>
            <rect x="-5" y="-5" width="34" height="34" style={{ fill: '#d2222d' }} />
          </g>
        </svg>
      </div>
    );
  } else if (kind === 'enhance') {
    return (
      <div className={styles.statusContainer}>
        <svg id="Layer_1" data-name="Layer 1" viewBox="0 0 24 24">
          <defs>
            <clipPath id="clip-path">
              <circle cx="12" cy="12" r="12" style={{ fill: 'none' }} />
            </clipPath>
            <clipPath id="clip-path-2">
              <rect x="-5" y="-5" width="34" height="34" style={{ fill: 'none' }} />
            </clipPath>
            <clipPath id="clip-path-3">
              <rect width="24" height="24" style={{ fill: 'none' }} />
            </clipPath>
            <clipPath id="clip-path-10">
              <rect width="12" height="24" style={{ fill: 'none' }} />
            </clipPath>
          </defs>
          <g style={{ clipPath: 'url(#clip-path)' }}>
            <g style={{ isolation: 'isolate' }}>
              <g style={{ clipPath: 'url(#clip-path-2)' }}>
                <g style={{ clipPath: 'url(#clip-path-3)' }}>
                  <circle
                    cx="12"
                    cy="12"
                    r="12"
                    style={{
                      fill: 'none',
                      stroke: '#ffbf00',
                      strokeLinejoin: 'round',
                      strokeWidth: '5px'
                    }}
                  />
                </g>
              </g>
            </g>
          </g>
          <g style={{ clipPath: 'url(#clip-path)' }}>
            <g style={{ isolation: 'isolate' }}>
              <g style={{ clipPath: 'url(#clip-path-3)' }}>
                <g style={{ clipPath: 'url(#clip-path)' }}>
                  <g style={{ clipPath: 'url(#clip-path-3)' }}>
                    <g style={{ isolation: 'isolate' }}>
                      <g style={{ clipPath: 'url(#clip-path-2)' }}>
                        <g style={{ clipPath: 'url(#clip-path-3)' }}>
                          <circle
                            cx="12"
                            cy="12"
                            r="12"
                            style={{
                              fill: 'none',
                              stroke: '#ffbf00',
                              strokeLinejoin: 'round',
                              strokeWidth: '5px'
                            }}
                          />
                        </g>
                      </g>
                    </g>
                  </g>
                </g>
                <g style={{ clipPath: 'url(#clip-path-10)' }}>
                  <rect x="-5" y="-5" width="22" height="34" style={{ fill: '#ffbf00' }} />
                </g>
              </g>
            </g>
          </g>
        </svg>
      </div>
    );
  } else if (kind === 'continue') {
    return (
      <div className={styles.statusContainer}>
        <svg id="Layer_1" data-name="Layer 1" viewBox="0 0 24 24">
          <defs>
            <clipPath id="clip-path">
              <circle cx="12" cy="12" r="12" style={{ fill: 'none' }} />
            </clipPath>
            <clipPath id="clip-path-2">
              <rect x="-5" y="-5" width="34" height="34" style={{ fill: 'none' }} />
            </clipPath>
            <clipPath id="clip-path-3">
              <rect width="24" height="24" style={{ fill: 'none' }} />
            </clipPath>
          </defs>
          <g style={{ clipPath: 'url(#clip-path)' }}>
            <g style={{ isolation: 'isolate' }}>
              <g style={{ clipPath: 'url(#clip-path-2)' }}>
                <g style={{ clipPath: 'url(#clip-path-3)' }}>
                  <circle
                    cx="12"
                    cy="12"
                    r="12"
                    style={{
                      fill: 'none',
                      stroke: '#238823',
                      strokeLinejoin: 'round',
                      strokeWidth: '5px'
                    }}
                  />
                </g>
              </g>
            </g>
          </g>
        </svg>
      </div>
    );
  }
  throw Error('Unimplemented status');
}



// WEBPACK FOOTER //
// ./src/ui/screens/manager-results-screen/manager-results-screen.tsx